import { attr, belongsTo } from "@ember-data/model";
import dayjs from "dayjs";
import {
  validateDate,
  validateNumber,
  validatePresence
} from "ember-changeset-validations/validators";
import type IntlService from "ember-intl/services/intl";
import JournalEvent from "parog-web/models/journal-event";
import tWithDefault from "parog-web/utils/t-with-default";
import type DpSessionDeclaredJournalEvent from "./dp-session-declared-journal-event";

//---- Automatically generated, don't update this part ----

export default class DpMeasuredJournalEvent extends JournalEvent {
  static override type = "dp-measured-journal-event";

  /**
   * Dialysis moment, before/cycle/end
   */
  @attr("string") declare moment: string;

  /**
   * Weight before the session
   */
  @attr("number") declare beforeWeight: number;

  /**
   * Total volume to inject before the session
   */
  @attr("number") declare beforeTotalVolumeToInject: number;

  /**
   * Arterial systolic pressure before the session
   */
  @attr("number") declare beforeArterialSystolicPressure: number;

  /**
   * Arterial diastolic pressure before the session
   */
  @attr("number") declare beforeArterialDiastolicPressure: number;

  /**
   * Heart rate before the session
   */
  @attr("number") declare beforeHeartRate: number;

  /**
   * Body temperature before the session
   */
  @attr("number") declare beforeBodyTemperature: number;

  /**
   * Orifice aspect before the session , painful/hardening/feeling of heat/clean and dry
   */
  @attr("string") declare beforeOrificeAspect: string;

  /**
   * Infused volume of the pocket in the cycle
   */
  @attr("number") declare cyclePocketInfusedVolume: number;

  /**
   * Drained volume of the pocket in the cycle
   */
  @attr("number") declare cyclePocketDrainedVolume: number;

  /**
   * Asoect of the pocket, clear/cloudy/pinkish/fibrinous
   */
  @attr("string") declare cyclePocketAspect: string;

  /**
   * Output weight end of the session
   */
  @attr("number") declare endOutputWeight: number;

  /**
   * Arterial systolic pressure end of the session
   */
  @attr("number") declare endArterialSystolicPressure: number;

  /**
   * Arterial diastolic pressure end of the session
   */
  @attr("number") declare endArterialDiastolicPressure: number;

  /**
   * Heart rate end of the session
   */
  @attr("number") declare endHeartRate: number;

  @belongsTo("dp-session-declared-journal-event", {
    async: false,
    inverse: "dpMeasuredJournalEvents"
  })
  declare dpSessionDeclaredJournalEvent: DpSessionDeclaredJournalEvent;

  static override validations(intl: IntlService) {
    return {
      occurredAt: [
        validatePresence({
          presence: true,
          description: tWithDefault(
            intl,
            "validations.dp-measured-journal-event.occurred-at",
            "La date"
          )
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date",
          onOrAfter: new Date("01/01/1900")
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée plus de 50 ans en avance. Veuillez modifier la date",
          onOrBefore: dayjs().add(50, "years").toDate()
        })
      ],
      beforeWeight: validateNumber({
        allowBlank: true,
        gte: 0,
        description: intl.t(
          "validations.dp-measured-journal-event.before-weight"
        )
      }),
      beforeTotalVolumeToInject: validateNumber({
        allowBlank: true,
        gte: 0,
        description: intl.t(
          "validations.dp-measured-journal-event.before-total-volume-to-inject"
        )
      }),
      beforeArterialSystolicPressure: validateNumber({
        allowBlank: true,
        gte: 0,
        description: intl.t(
          "validations.dp-measured-journal-event.before-arterial-systolic-pressure"
        )
      }),
      beforeArterialDiastolicPressure: validateNumber({
        allowBlank: true,
        gte: 0,
        description: intl.t(
          "validations.dp-measured-journal-event.before-arterial-diastolic-pressure"
        )
      }),
      beforeHeartRate: validateNumber({
        allowBlank: true,
        gte: 0,
        description: intl.t(
          "validations.dp-measured-journal-event.before-heart-rate"
        )
      }),
      beforeBodyTemperature: validateNumber({
        allowBlank: true,
        gte: 0,
        description: intl.t(
          "validations.dp-measured-journal-event.before-body-temperature"
        )
      }),
      cyclePocketInfusedVolume: validateNumber({
        allowBlank: true,
        gte: 0,
        description: intl.t(
          "validations.dp-measured-journal-event.cycle-pocket-infused-volume"
        )
      }),
      cyclePocketDrainedVolume: validateNumber({
        allowBlank: true,
        gte: 0,
        description: intl.t(
          "validations.dp-measured-journal-event.cycle-pocket-drained-volume"
        )
      }),
      endOutputWeight: validateNumber({
        allowBlank: true,
        gte: 0,
        description: intl.t(
          "validations.dp-measured-journal-event.end-output-weight"
        )
      }),
      endArterialSystolicPressure: validateNumber({
        allowBlank: true,
        gte: 0,
        description: intl.t(
          "validations.dp-measured-journal-event.end-arterial-systolic-pressure"
        )
      }),
      endArterialDiastolicPressure: validateNumber({
        allowBlank: true,
        gte: 0,
        description: intl.t(
          "validations.dp-measured-journal-event.end-arterial-diastolic-pressure"
        )
      }),
      endHeartRate: validateNumber({
        allowBlank: true,
        gte: 0,
        description: intl.t(
          "validations.dp-measured-journal-event.end-heart-rate"
        )
      })
    };
  }

  //---- End of generated code ----
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "dp-measured-journal-event": DpMeasuredJournalEvent;
  }
}
